// src/components/gestionEvento/AjustesForm.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Box, Typography, Snackbar, Alert, Paper, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AjustesManager from './Ajustes/AjustesManager';
import RegistroManager from './Ajustes/RegistroManager';


/**
 * TabPanel es un componente auxiliar para manejar el contenido de cada pestaña.
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ajustes-tabpanel-${index}`}
      aria-labelledby={`ajustes-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
          {/* Cambio: Remover Typography wrapper innecesario */}
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

/**
 * a11yProps se utiliza para la accesibilidad de las pestañas.
 */
function a11yProps(index) {
  return {
    id: `ajustes-tab-${index}`,
    'aria-controls': `ajustes-tabpanel-${index}`,
  };
}

/**
 * AjustesForm es el componente principal que contiene las pestañas para Ajustes de Evento y Ajustes de Registro.
 */
const AjustesForm = ({ competencia, setCompetencia, onDelete }) => {
  const [value, setValue] = useState(0); // Estado para rastrear la pestaña seleccionada
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Estado para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Manejar el cierre del Snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2, mb: 0.5 }}>
          Ajustes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configura los datos generales, registro y personalización de tu competencia.
        </Typography>
      </Box>

      {/* Tabs estilo “pill” y sticky (igual que PruebasForm) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          pb: 1.5,
          pt: 0.5,
          background: `linear-gradient(180deg, ${theme.palette.background.default} 70%, ${alpha(
            theme.palette.background.default,
            0
          )} 100%)`,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            px: { xs: 0.75, sm: 1 },
            py: 0.5,
            borderRadius: 999,
            display: 'inline-flex',
            border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(6px)',
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Ajustes Tabs"
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            TabIndicatorProps={{
              sx: {
                height: '100%',
                borderRadius: 999,
                zIndex: -1,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
            }}
            sx={{
              minHeight: 44,
              '& .MuiTabs-flexContainer': { gap: 0.5 },
            }}
          >
            <Tab
              label="Ajustes de Evento"
              {...a11yProps(0)}
              sx={{
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 700,
                px: { xs: 1.5, sm: 2.25 },
                borderRadius: 999,
                color: 'text.secondary',
                '&.Mui-selected': { color: theme.palette.primary.main },
              }}
            />
            <Tab
              label="Ajustes de Registro"
              {...a11yProps(1)}
              sx={{
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 700,
                px: { xs: 1.5, sm: 2.25 },
                borderRadius: 999,
                color: 'text.secondary',
                '&.Mui-selected': { color: theme.palette.primary.main },
              }}
            />
          </Tabs>
        </Paper>
      </Box>

      {/* Panel contenedor con mismo estilo que PruebasForm */}
      <Box
        sx={{
          mt: 2,
          background: alpha(theme.palette.background.paper, 0.6),
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          borderRadius: { xs: 0, md: 3 },
          boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          overflow: 'hidden',
        }}
      >
        <TabPanel value={value} index={0}>
          <AjustesManager
            competencia={competencia}
            setCompetencia={setCompetencia}
            onDelete={onDelete}
            setSnackbar={setSnackbar}
          />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <RegistroManager
            competencia={competencia}
            setCompetencia={setCompetencia}
            setSnackbar={setSnackbar}
          />
        </TabPanel>
      </Box>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

AjustesForm.propTypes = {
  competencia: PropTypes.object.isRequired,
  setCompetencia: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default AjustesForm;
